import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getCurrentUser, loginWithPassword, registerUser } from "../utils/auth";
import { convertGuestSession } from "../utils/guestConversion";
import { clearGuestConversionState } from "../utils/storage";
import { BETA_ANALYTICS_EVENTS, trackBetaEvent } from "../utils/analytics";
import Logo from "../components/brand/Logo";
import Button from "../components/ui/Button";
import Card from "../components/ui/Card";
import FormField, { fieldClassName } from "../components/ui/FormField";

export default function Signup({ onSignup }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const shouldConvertGuest =
    location.state?.convertGuest ||
    new URLSearchParams(location.search).get("convert") === "guest";

  const finishSignupFlow = async () => {
    const converted = shouldConvertGuest
      ? await convertGuestSession(location.state)
      : false;

    if (converted) {
      clearGuestConversionState();
    }

    navigate(converted ? "/saved-shows" : "/quiz", {
      state: converted ? { convertedGuest: true } : undefined,
    });
  };

  const handleSignup = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setIsSubmitting(true);

    try {
      await registerUser({
        email,
        password,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      });
      trackBetaEvent(BETA_ANALYTICS_EVENTS.ACCOUNT_CREATED, {
        source: shouldConvertGuest ? "guest_conversion" : "signup",
      });
      await loginWithPassword(email, password);
      const currentUser = await getCurrentUser();
      await onSignup?.(currentUser);
      await finishSignupFlow();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Sign up failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mx-auto grid min-h-[calc(100vh-96px)] max-w-6xl gap-8 px-4 py-10 sm:px-6 lg:grid-cols-[1fr_460px] lg:items-center lg:px-8">
      <div className="hidden lg:block">
        <Logo size="hero" />
        <h1 className="mt-8 font-display text-6xl font-bold leading-tight text-hookd-cream">
          Save every crowned match.
        </h1>
        <div className="hookd-divider my-7 max-w-md">♕</div>
        <p className="max-w-xl text-lg leading-8 text-hookd-muted">
          Create your account to keep quiz results, recommendation history, and saved shows.
        </p>
      </div>

      <Card className="p-6 sm:p-8" glow="purple">
      <h2 className="font-display text-3xl font-bold mb-4 text-hookd-cream">Create your Hook’d account</h2>
      {shouldConvertGuest && (
        <p className="mb-4 rounded-hookd border border-hookd-gold/30 bg-hookd-gold/10 p-3 text-sm text-hookd-cream">
          Your current quiz and matches will be saved to this account.
        </p>
      )}
      <form onSubmit={handleSignup} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label="First name">
            <input
              type="text"
              placeholder="First name"
              className={fieldClassName}
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              autoComplete="given-name"
              required
            />
          </FormField>
          <FormField label="Last name">
            <input
              type="text"
              placeholder="Last name"
              className={fieldClassName}
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
              autoComplete="family-name"
              required
            />
          </FormField>
        </div>
        <FormField label="Email">
          <input
            type="email"
            placeholder="you@empress.com"
            className={fieldClassName}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            required
          />
        </FormField>
        <FormField label="Password">
          <input
            type="password"
            placeholder="Password"
            className={fieldClassName}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
          />
        </FormField>
        <FormField label="Confirm password">
          <input
            type="password"
            placeholder="Confirm password"
            className={fieldClassName}
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            autoComplete="new-password"
            minLength={8}
            required
          />
        </FormField>

        {error && <p className="text-sm text-red-300">{error}</p>}

        <Button
          type="submit"
          loading={isSubmitting}
          className="w-full"
        >
          {isSubmitting ? "Creating Account..." : "Create Account"}
        </Button>
      </form>

      <p className="text-sm text-center mt-5 text-hookd-muted">
        Already have an account?{" "}
        <Link
          to="/login"
          state={location.state}
          className="inline-flex min-h-11 items-center text-hookd-empress font-semibold underline"
        >
          Log in
        </Link>
      </p>
      </Card>
    </div>
  );
}
